import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { netsuiteCustomerDropdownSelectors } from '@vy/app-selectors';
import {
  INTEGRATION_PROVIDERS,
  type ErpIntermediateMetadata,
  type NetSuiteCustomer,
  type NewCustomer,
  type UpdateCustomer,
  type CustomerIntegrationEntity,
  type NetSuiteCustomerEntity,
  SYNC_STATUSES,
} from '@vy/types';
import {
  isEmptyArray,
  isEmptyString,
  isNotEmptyArray,
  isNotEmptyString,
  isNotNullOrUndefined,
  uniqueExtract,
  Optional,
  Nullable,
  isNullOrUndefined,
} from '@vy/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useNetSuiteCustomers } from '../../../../hooks/requests/integrations';
import { useDebounce } from '../../../../hooks/use-debounce';
import { useFormContext } from 'react-hook-form';
import { findIntegrationByProviderInEntities } from '@vy/services';

const DEBOUNCE_DELAY = 800;

type NetSuiteCustomersDropdownProps = {
  showDropdown: boolean;
};

export function NetSuiteCustomersDropdown({ showDropdown }: NetSuiteCustomersDropdownProps) {
  const [searchInput, setSearchValue] = useState<string | undefined>(undefined);
  const [allCustomers, setAllCustomers] = useState<NetSuiteCustomer[]>([]);
  const [selectedErp, setSelectedErp] = useState<ErpIntermediateMetadata | null>(null);
  const debouncedSearchValue = useDebounce(searchInput, DEBOUNCE_DELAY);

  const { setValue, getValues } = useFormContext<NewCustomer | UpdateCustomer>();

  const { data: { customers } = {}, isFetching } = useNetSuiteCustomers(debouncedSearchValue, {
    enabled: showDropdown,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (isNotEmptyArray(customers)) {
      setAllCustomers((prevCustomers) => mergeUniqueCustomers(prevCustomers, customers));
    }
  }, [customers]);

  const customersErpData = useMemo(() => netSuiteCustomersToErpData(allCustomers), [allCustomers]);

  const integrationEntities = getValues('integrationEntities') ?? [];
  const netSuiteEntity = findIntegrationByProviderInEntities<NetSuiteCustomerEntity>(
    integrationEntities,
    INTEGRATION_PROVIDERS.netSuite,
  );
  const currentErpEntity = toErpIntermediateMetadata(netSuiteEntity);

  useEffect(() => {
    if (currentErpEntity && !selectedErp) {
      setSelectedErp(currentErpEntity);
    }
  }, [currentErpEntity, selectedErp]);

  const updateNetSuiteEntity = (erpMetadata: ErpIntermediateMetadata | null) => {
    setSelectedErp(erpMetadata);
    const currentEntities = getValues('integrationEntities') ?? [];
    const filteredEntities = removeNetSuiteEntity(currentEntities);

    if (isNullOrUndefined(erpMetadata)) {
      setValue('integrationEntities', filteredEntities, { shouldDirty: true });

      return;
    }

    const newEntity = createNetSuiteEntity(erpMetadata);
    setValue('integrationEntities', [...filteredEntities, newEntity], { shouldDirty: true });
  };

  const handleCustomerChange = (_: React.SyntheticEvent, value: ErpIntermediateMetadata | null) => {
    if (isNullOrUndefined(value)) {
      updateNetSuiteEntity(null);
    } else {
      updateNetSuiteEntity(value);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmptyString(e.target.value)) {
      updateNetSuiteEntity(null);
    }
    setSearchValue(e.target.value);
  };

  return (
    <Autocomplete
      disabled={!showDropdown}
      options={customersErpData}
      getOptionLabel={(option) => option?.erpName ?? ''}
      getOptionDisabled={(option) => option.erpId === selectedErp?.erpId}
      onChange={handleCustomerChange}
      value={selectedErp}
      data-vy={netsuiteCustomerDropdownSelectors.root}
      renderInput={(params) => (
        <TextField
          {...params}
          label="NS Customer ID"
          onChange={handleSearchChange}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <React.Fragment>
                  {isFetching && <CircularProgress color="inherit" size={20} />}
                  {params.slotProps.input.endAdornment}
                </React.Fragment>
              ),
            },
            inputLabel: {
              shrink: true,
            },
          }} />
      )}
    />
  );
}

function mergeUniqueCustomers(existingCustomers: NetSuiteCustomer[], newCustomers: NetSuiteCustomer[]) {
  const existingIds = uniqueExtract(existingCustomers, 'id');

  return [...existingCustomers, ...newCustomers.filter((customer) => !existingIds.includes(customer.id))];
}

function netSuiteCustomersToErpData(customers: NetSuiteCustomer[] | undefined) {
  return isEmptyArray(customers)
    ? []
    : customers.map<ErpIntermediateMetadata>(({ id, entityId, companyName, aliasName }) => ({
        erpId: id,
        erpName: formatErpName(entityId, companyName, aliasName),
        displayId: entityId,
        integrationSource: INTEGRATION_PROVIDERS.netSuite,
      }));
}

function formatErpName(entityId: string, companyName: string, aliasName: string) {
  const formattedName = isNotEmptyString(companyName) ? companyName : aliasName;

  return isEmptyString(formattedName) ? entityId : `${entityId} ${formattedName}`;
}

function removeNetSuiteEntity(entities: CustomerIntegrationEntity[] | undefined | null) {
  return isNotNullOrUndefined(entities)
    ? entities.filter((entity) => entity?.integrationProvider !== INTEGRATION_PROVIDERS.netSuite)
    : [];
}

function toErpIntermediateMetadata(
  entity: Optional<NetSuiteCustomerEntity>,
): Nullable<ErpIntermediateMetadata> {
  if (isNullOrUndefined(entity)) {
    return null;
  }

  return {
    erpId: entity.integrationExternalId,
    erpName: entity.metadata.name,
    displayId: entity.metadata.displayId,
    integrationSource: INTEGRATION_PROVIDERS.netSuite,
  };
}

function createNetSuiteEntity(erpMetadata: ErpIntermediateMetadata): CustomerIntegrationEntity {
  return {
    integrationProvider: INTEGRATION_PROVIDERS.netSuite,
    integrationExternalId: erpMetadata.erpId,
    updatedAt: new Date(),
    syncStatus: SYNC_STATUSES.None,
    metadata: {
      name: erpMetadata.erpName,
      displayId: erpMetadata.displayId,
    },
  };
}
